import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";
import { FaTimes } from "react-icons/fa";

/**
 * Drawer trượt có hiệu ứng. placement:
 *   - "right" (mặc định): trượt từ phải, dùng width/minWidth.
 *   - "bottom": trượt từ dưới lên, dùng height (vd "78vh"), bo góc trên.
 * Giữ mounted lúc đóng để animate-out chạy hết (không bị "flash").
 */
const SlideDrawer = ({
  show, onHide, title, children, footer, bodyClassName = "", showClose = true,
  placement = "right", width = "64%", minWidth = "720px", height = "78vh",
  backdropClick = true, fixedHeight = false,
}) => {
  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);
  const timer = useRef();
  const isBottom = placement === "bottom";

  useEffect(() => {
    clearTimeout(timer.current);
    if (show) {
      setMounted(true);
      timer.current = setTimeout(() => setVisible(true), 20);
    } else {
      setVisible(false);
      timer.current = setTimeout(() => setMounted(false), 320);
    }
    return () => clearTimeout(timer.current);
  }, [show]);

  useEffect(() => {
    if (mounted) document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, [mounted]);

  if (!mounted) return null;

  const hiddenTransform = isBottom ? "tw-translate-y-full" : "tw-translate-x-full";
  const shownTransform = isBottom ? "tw-translate-y-0" : "tw-translate-x-0";
  const posClass = isBottom
    ? "tw-bottom-0 tw-left-0 tw-right-0 tw-rounded-t-geist-lg"
    : "tw-top-0 tw-right-0 tw-h-full";
  // Bottom: mặc định cao theo nội dung (cap max-height). Với fixedHeight → khóa
  // đúng `height` để đổi tab không làm drawer thò thụt (body tự cuộn phần dư).
  const panelStyle = isBottom
    ? (fixedHeight ? { height } : { maxHeight: height })
    : { width, minWidth };
  // Body fill hết chiều cao khi: side-drawer, hoặc bottom + fixedHeight.
  const bodyFill = !isBottom || fixedHeight;

  return createPortal(
    <>
      <div
        onClick={backdropClick ? onHide : undefined}
        className={`tw-fixed tw-inset-0 tw-bg-black/40 tw-z-[1040] tw-transition-opacity tw-duration-300 ${visible ? "tw-opacity-100" : "tw-opacity-0"}`}
        aria-hidden="true"
      />
      <div
        role="dialog"
        aria-modal="true"
        style={panelStyle}
        className={`geist-scope tw-fixed ${posClass} tw-bg-geist-bg tw-z-[1050] tw-shadow-geist-modal tw-flex tw-flex-col tw-transition-transform tw-duration-300 tw-ease-out ${visible ? shownTransform : hiddenTransform}`}
      >
        {title !== undefined && (
          <div className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-px-5 tw-py-3 tw-border-b tw-border-solid tw-border-geist-border tw-bg-geist-bg tw-flex-shrink-0">
            <div className="tw-flex-1 tw-min-w-0">{title}</div>
            {showClose && (
              <div role="button" tabIndex={0} onClick={onHide}
                className="tw-flex-shrink-0 tw-p-1.5 tw-rounded-geist-sm tw-text-geist-fg-3 hover:tw-text-geist-fg hover:tw-bg-geist-100 tw-transition tw-cursor-pointer">
                <FaTimes size={16} />
              </div>
            )}
          </div>
        )}
        <div className={`${bodyFill ? "tw-flex-1 tw-min-h-0" : "tw-min-h-0"} tw-overflow-y-auto tw-px-6 tw-py-5 ${bodyClassName}`}>{children}</div>
        {footer && (
          <div className="tw-px-6 tw-py-4 tw-border-t tw-border-solid tw-border-geist-border tw-bg-geist-bg tw-flex-shrink-0">{footer}</div>
        )}
      </div>
    </>,
    document.body
  );
};

const cssSize = PropTypes.oneOfType([PropTypes.string, PropTypes.number]);

SlideDrawer.propTypes = {
  show: PropTypes.bool,
  onHide: PropTypes.func,
  title: PropTypes.node,
  children: PropTypes.node,
  footer: PropTypes.node,
  bodyClassName: PropTypes.string,
  showClose: PropTypes.bool,
  placement: PropTypes.oneOf(["right", "bottom"]),
  width: cssSize,
  minWidth: cssSize,
  height: cssSize,
  backdropClick: PropTypes.bool,
  fixedHeight: PropTypes.bool,
};

export default SlideDrawer;
